import React, { useState, useEffect, useRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Shield, RefreshCw, Wifi, WifiOff } from 'lucide-react'
import {
  generateQRCodeData,
  getCurrentTimeWindow,
  getTimeRemainingInWindow,
  QRCodeCache
} from '../../lib/totp'
import './AnimatedQRCode.css'

interface AnimatedQRCodeProps {
  orderId: string
  orderSecret: string
  size?: number
  showTimer?: boolean
  offlineMode?: boolean
}

const AnimatedQRCode: React.FC<AnimatedQRCodeProps> = ({
  orderId,
  orderSecret,
  size = 250,
  showTimer = true,
  offlineMode = false
}) => {
  const [qrData, setQrData] = useState<string>('')
  const [timeRemaining, setTimeRemaining] = useState<number>(15)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const cacheRef = useRef<QRCodeCache | null>(null)

  // Initialize QR code cache
  useEffect(() => {
    const initializeCache = async () => {
      const cache = new QRCodeCache(orderId, orderSecret)

      // Try to load from storage first (offline support)
      const loaded = cache.loadFromStorage()

      if (!loaded || !offlineMode) {
        // Initialize fresh cache
        await cache.initialize()
        cache.saveToStorage()
      }

      cacheRef.current = cache

      // Set initial code
      const initialCode = cache.getCode(getCurrentTimeWindow())
      if (initialCode) {
        setQrData(initialCode)
      }
    }

    initializeCache()
  }, [orderId, orderSecret, offlineMode])

  // Update QR code every 15 seconds
  useEffect(() => {
    const updateQRCode = async () => {
      setIsRefreshing(true)

      try {
        if (cacheRef.current) {
          // Use cached code
          const currentWindow = getCurrentTimeWindow()
          const cachedCode = cacheRef.current.getCode(currentWindow)

          if (cachedCode) {
            setQrData(cachedCode)

            // Refresh cache if needed
            if (cacheRef.current.needsRefresh()) {
              await cacheRef.current.refresh()
              cacheRef.current.saveToStorage()
            }
          } else {
            // Fallback: generate fresh code
            const freshCode = await generateQRCodeData(orderId, orderSecret)
            setQrData(freshCode)
          }
        } else {
          // No cache available: generate fresh code
          const freshCode = await generateQRCodeData(orderId, orderSecret)
          setQrData(freshCode)
        }
      } catch (error) {
        console.error('Failed to update QR code:', error)
      }

      setTimeout(() => setIsRefreshing(false), 500)
    }

    // Update immediately
    updateQRCode()

    // Update every 15 seconds
    const interval = setInterval(updateQRCode, 15000)

    return () => clearInterval(interval)
  }, [orderId, orderSecret])

  // Update timer every second
  useEffect(() => {
    const updateTimer = () => {
      setTimeRemaining(getTimeRemainingInWindow())
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [])

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  if (!qrData) {
    return (
      <div
        className="flex items-center justify-center bg-secret-bg-secondary rounded-lg"
        style={{ width: size, height: size }}
      >
        <RefreshCw className="animate-spin text-secret-primary" size={48} />
      </div>
    )
  }

  return (
    <div className="animated-qr-container">
      {/* QR Code Container with animations */}
      <div
        className={`qr-code-wrapper ${isRefreshing ? 'refreshing' : ''}`}
        style={{ width: size, height: size }}
      >
        {/* Pulsing background effect */}
        <div className="qr-pulse-effect" />

        {/* QR Code */}
        <div className="qr-code-content">
          <QRCodeSVG
            value={qrData}
            size={size - 40}
            level="H"
            includeMargin={true}
            fgColor="#000000"
            bgColor="#ffffff"
          />

          {/* Sweeping line animation (anti-fraud effect) */}
          <div className="qr-sweep-line" />
        </div>

        {/* Corner markers for authenticity */}
        <div className="qr-corner qr-corner-tl" />
        <div className="qr-corner qr-corner-tr" />
        <div className="qr-corner qr-corner-bl" />
        <div className="qr-corner qr-corner-br" />
      </div>

      {/* Security Info */}
      <div className="qr-info-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="text-green-500" size={16} />
            <span className="text-xs text-secret-text-secondary">
              Secure • Rotating Code
            </span>
          </div>

          {!isOnline && (
            <div className="flex items-center space-x-1 text-orange-400">
              <WifiOff size={14} />
              <span className="text-xs">Offline</span>
            </div>
          )}
        </div>

        {showTimer && (
          <div className="qr-timer-bar">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-secret-text-tertiary">Code refreshes in</span>
              <span className="text-xs font-mono font-bold text-white">{timeRemaining}s</span>
            </div>
            <div className="timer-progress-track">
              <div
                className="timer-progress-fill"
                style={{
                  width: `${(timeRemaining / 15) * 100}%`,
                  transition: 'width 1s linear'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Order ID Display */}
      <div className="qr-order-id">
        <span className="text-xs text-secret-text-tertiary">Order</span>
        <span className="text-sm font-mono font-bold text-white ml-2">
          #{orderId.slice(0, 8).toUpperCase()}
        </span>
      </div>

      {/* Anti-fraud warning */}
      <div className="qr-warning-text">
        <div className="flex items-start space-x-2 text-xs text-secret-text-tertiary">
          <Shield size={12} className="mt-0.5 flex-shrink-0" />
          <div>
            <strong className="text-orange-400">Anti-fraud Protection:</strong>{' '}
            This code changes every 15 seconds. Do not share screenshots.
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnimatedQRCode
